'use client'

import { memo, useEffect, useRef, useState, type ReactNode } from 'react'
import * as styles from './Sidebar.css'
import {
  isPageScrolledStore,
  isSidebarScrolledStore,
} from '../Clause/scroll-store'
import { useStore } from '@shared/lib/store'
import * as ScrollArea from '@radix-ui/react-scroll-area'
import { TocContext } from './Toc'
import { scrollHintStore } from './Sidebar.stores'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shared/components/Tooltip/Tooltip'
import { useSidebarScroll } from '@/components/Norm/useSidebarScroll'

import { type SidebarProps } from '@/components/Norm/NormLayout'
import Heading from '@shared/components/Heading/Heading'
import { Row, Stack } from '@christiankaindl/lyts'
import { MoreHorizontalIcon, SearchIcon } from 'lucide-react'
import Button from '@shared/components/Button/Button'
import { useSearchOverlay } from '@/app/[category]/[subCategory]/[norm]/SearchOverlay'
import { BookMarked as LawIcon } from 'lucide-react'
import { theme } from '@/styles/theme.css'
import Text from '@shared/components/Text/Text'
import { Menu, MenuButton } from '@shared/components/Menu'
import { useTooltipStore } from '@ariakit/react'
import SidebarContent from './Sidebar.server'

export default function ClientSidebar({
  className,
  ...props
}: SidebarProps & { className?: string }) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  useSidebarScroll(scrollContainerRef)

  return (
    <ScrollArea.Root
      style={{ height: '100%', marginLeft: -21 }}
      type='auto'
      className={className}
    >
      <ScrollArea.Viewport
        ref={scrollContainerRef}
        className={styles.viewport}
        id='sidebar-desktop-scroll-container'
      >
        <TocContext.Provider value={{ ref: scrollContainerRef }}>
          <SidebarContent {...props} />
        </TocContext.Provider>
      </ScrollArea.Viewport>
      <CondScrollbar />
    </ScrollArea.Root>
  )
}

export function Header({
  forceShowTitle,
  title,
  className,
  children,
  abbreviation,
}: {
  forceShowTitle: boolean
  title: string
  children: ReactNode
  className?: string
  abbreviation?: string
}) {
  const pageScrolled = useStore(isPageScrolledStore)
  const sidebarScrolled = useStore(isSidebarScrolledStore)

  // Stores are module singletons — on client-side navigation (or HMR) they
  // keep stale `true` values from the previous page while the server always
  // renders with `false`. Ignore store values until after hydration so the
  // first render matches the server HTML.
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => setHydrated(true), [])

  const showTitle = forceShowTitle || (hydrated && pageScrolled)
  const showHeader = showTitle || (hydrated && sidebarScrolled)

  const tooltip = useTooltipStore()

  function scrollToTop() {
    document.documentElement.scrollTo({ top: 0 })
    document
      .getElementById('sidebar-desktop-scroll-container')
      ?.scrollTo({ top: 0 })
    tooltip.hide()
  }

  return (
    <Row asChild xAlign='space-between' gap={0.5}>
      <header
        className={className}
        style={{ contain: 'unset', transition: 'height 300ms' }}
      >
        <Heading
          weight={700}
          maxLines={1}
          h={5}
          as='h3'
          style={{
            opacity: showHeader ? 1 : 0,
            transition: 'opacity 120ms',
            fontSize: '1.1em',
            wordBreak: 'break-all',
            flexShrink: 0,
            textWrap: 'balance',
            maxWidth: hydrated && sidebarScrolled ? '80%' : '100%',
          }}
        >
          <Tooltip placement='bottom-start' store={tooltip}>
            <TooltipTrigger
              onClick={scrollToTop}
              disabled={!hydrated || !pageScrolled}
              render={
                <Row
                  tabIndex={0}
                  gap={0.25}
                  style={{ color: theme.colors.text.brand, cursor: 'default' }}
                />
              }
            >
              <LawIcon width={16} />
              <Text
                as='p' // using span breaks the maxLines prop, so we use p
                color={theme.colors.text.brand}
                weight={600}
                maxLines={1}
              >
                {abbreviation ?? title}
              </Text>
            </TooltipTrigger>
            <TooltipContent
              render={<Stack gap='2px' />}
              style={{ textAlign: 'left' }}
              onClick={scrollToTop}
            >
              <b>{title}</b>
              <i>Zum Seitenanfang springen</i>
            </TooltipContent>
          </Tooltip>
        </Heading>
        <div
          style={{
            visibility: hydrated && sidebarScrolled ? undefined : 'hidden',
          }}
        >
          {children}
        </div>
        <Separator />
      </header>
    </Row>
  )
}
function Separator() {
  const isScrolled = useStore(isSidebarScrolledStore)
  return (
    <hr
      style={{
        visibility: isScrolled ? 'visible' : 'hidden',
        position: 'absolute',
        bottom: 0,
        left: 21,
        width: 'calc(100% - 42px)',
      }}
    />
  )
}

export const ActionRow = memo(function ActionRow({
  menu,
  showSearch,
}: {
  menu: ReactNode
  showSearch: boolean
  headerButtons?: ReactNode
  noToc?: boolean
}) {
  const dialog = useSearchOverlay((state) => state.dialog)
  return (
    <Row gap={0.5} key='action-buttons' bleedLeft={10}>
      {showSearch && (
        <Button
          onClick={() => {
            dialog?.show()
          }}
          variant='flat'
          size='small'
          icon={SearchIcon}
          title='Durchsuchen'
        />
      )}
      <Menu>
        <MenuButton size='small' variant='flat' square title='Mehr'>
          <MoreHorizontalIcon size={20} />
        </MenuButton>
        {menu}
      </Menu>
    </Row>
  )
})

export function CondScrollbar() {
  const isScrolled = useStore(isPageScrolledStore)

  if (!isScrolled) return null

  return (
    <ScrollArea.Scrollbar className={styles.scrollbar}>
      <TocScrollHighlight />
      <ScrollArea.Thumb className={styles.thumb} />
    </ScrollArea.Scrollbar>
  )
}

export function TocScrollHighlight() {
  const showHint = useStore(scrollHintStore)

  return (
    <Tooltip placement='right'>
      <TooltipTrigger
        render={
          <button
            style={{
              transform: `translateY(${showHint.top}px)`,
            }}
            className={styles.tocScrollHighlight}
            onClick={() => {
              showHint.scrollToElement()
            }}
            onPointerDown={(event) => {
              event.stopPropagation()
            }}
          />
        }
      />
      <TooltipContent>{showHint.label}</TooltipContent>
    </Tooltip>
  )
}
